import React, { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../../api.js'
import { Card, Field, Alert, JobProgress, useJobElapsed, usePolling } from '../../lib.jsx'
import { pagePxDims, injectEditingSupport, isEditableKind, fitWidthZoom, fitHeightZoom } from './pageviewer.js'
import FlowchartEditor from './FlowchartEditor.jsx'

const KIND_PL = { heading: 'nagłówek', paragraph: 'akapit', quote: 'cytat', bullet_list: 'lista punktowana',
  numbered_list: 'lista numerowana', table: 'tabela', image: 'obraz', chart: 'wykres', flowchart: 'diagram' }

function useJob(jobId, opts) {
  return usePolling(api.job, jobId, opts)
}

function unitPreviewText(unit) {
  const c = unit?.content || {}
  return c.text || (c.items || []).join(' · ') ||
    (unit?.kind === 'table' ? `${(c.headers || []).length} kolumn × ${(c.rows || []).length} wierszy` :
    (unit?.kind === 'image' ? c.image_ref : (c.title || '(treść)')))
}

// ---- popover generyczny: przycisk + panel otwierany na klik, zamykany klikiem na zewnątrz ----
function Popover({ label, badge, children, disabled }) {
  const [open, setOpen] = useState(false)
  const ref = useRef()
  useEffect(() => {
    if (!open) return undefined
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])
  return (
    <div className="popover-wrap" ref={ref}>
      <button type="button" className="btn btn-sm btn-ghost" disabled={disabled} onClick={() => setOpen(o => !o)}>
        {label}{badge ? ` (${badge})` : ''} {open ? '▴' : '▾'}
      </button>
      {open && <div className="popover-panel" onClick={e => e.stopPropagation()}>{children}</div>}
    </div>
  )
}

function AssignPicker({ page, template, onAssign }) {
  const [slot, setSlot] = useState('')
  if (!page) return <div className="muted small">Wybierz najpierw stronę docelową.</div>
  const partial = (template?.partials || []).find(p => p.slug === page.partial_slug)
  const slots = partial?.slots || []
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <select value={slot} onChange={e => setSlot(e.target.value)}>
        <option value="">— slot na stronie {page.order_index + 1} —</option>
        {slots.map(s => <option key={s.id} value={s.data_content}>{s.label} ({s.data_content})</option>)}
      </select>
      <button type="button" className="btn btn-sm" disabled={!slot} onClick={() => { onAssign(slot); setSlot('') }}>Przypisz</button>
    </div>
  )
}

// ---- pływający mini-toolbar formatowania (nad zaznaczeniem w edytowanym elemencie) ----
function RteToolbar({ toolbar, onExec }) {
  if (!toolbar || !toolbar.visible) return null
  const btn = (cmd, label, active) => (
    <button type="button" className={`rte-btn ${active ? 'active' : ''}`}
      onMouseDown={e => e.preventDefault()} onClick={() => onExec(cmd)}>{label}</button>
  )
  const f = toolbar.format || {}
  return (
    <div className="rte-toolbar" style={{ top: toolbar.top, left: toolbar.left }}>
      {btn('bold', 'B', f.bold)}
      {btn('italic', 'I', f.italic)}
      {btn('underline', 'U', f.underline)}
      <span className="rte-sep" />
      {btn('insertOrderedList', '1.2.3.', f.ol)}
      {btn('insertUnorderedList', '• ≡', f.ul)}
      <span className="rte-sep" />
      {btn('createLink', '🔗', false)}
    </div>
  )
}

// ---- modal dla kindów bez live-edit (tabela/wykres/diagram) + komentarze (dowolny kind) ----
function UnitDetailModal({ projectId, unit, onClose, onSaved }) {
  const isFlowchart = unit.kind === 'flowchart'
  const [rawJson, setRawJson] = useState(JSON.stringify(unit.content || {}, null, 2))
  const [flowContent, setFlowContent] = useState(() => ({
    type: 'flowchart', orientation: 'vertical', nodes: [], edges: [], ...(unit.content || {}),
  }))
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState('')
  const structural = !isEditableKind(unit.kind) && unit.kind !== 'image'

  useEffect(() => {
    api.comments(projectId, unit.id).then(setComments).catch(() => {})
  }, [unit.id])

  const save = async () => {
    setErr(''); setBusy(true)
    try {
      let content
      if (isFlowchart) {
        content = flowContent
      } else {
        try { content = JSON.parse(rawJson) } catch { throw new Error('Nieprawidłowy JSON') }
      }
      const updated = await api.patchUnit(projectId, unit.id, content)
      onSaved(updated)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const addComment = async () => {
    if (!newComment.trim()) return
    try { const c = await api.addComment(projectId, unit.id, newComment.trim()); setComments(cs => [...cs, c]); setNewComment('') }
    catch (e) { setErr(e.message) }
  }
  const cycleStatus = async (c) => {
    const next = c.status === 'open' ? 'resolved' : c.status === 'resolved' ? 'reopened' : 'open'
    try { const updated = await api.patchComment(projectId, c.id, next); setComments(cs => cs.map(x => x.id === c.id ? updated : x)) }
    catch (e) { setErr(e.message) }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="card-head">
          <h3>{KIND_PL[unit.kind] || unit.kind}</h3>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>Zamknij ✕</button>
        </div>
        <Alert kind="err">{err}</Alert>
        {isFlowchart ? (
          <>
            <FlowchartEditor content={flowContent} onChange={setFlowContent} />
            <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={save} style={{ margin: '14px 0 20px' }}>
              {busy ? 'Zapisywanie…' : 'Zapisz schemat'}
            </button>
          </>
        ) : structural ? (
          <>
            <Field label="Treść (JSON)" hint="Ten typ jednostki nie ma edytora na żywo — edycja przez surowy JSON.">
              <textarea value={rawJson} onChange={e => setRawJson(e.target.value)} rows={9} className="mono" />
            </Field>
            <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={save} style={{ marginBottom: 20 }}>
              {busy ? 'Zapisywanie…' : 'Zapisz treść'}
            </button>
          </>
        ) : (
          <div className="muted small" style={{ marginBottom: 18 }}>
            Ta treść edytowana jest bezpośrednio na podglądzie strony — kliknij element, aby ją zmienić.
          </div>
        )}
        <div className="divider" />
        <div className="lbl" style={{ marginBottom: 10 }}>Komentarze</div>
        {comments.length === 0 && <div className="muted small" style={{ marginBottom: 10 }}>Brak komentarzy.</div>}
        {comments.map(c => (
          <div key={c.id} className="kvp" style={{ alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div className="small">{c.body}</div>
              <div className="muted small">{new Date(c.created_at).toLocaleString('pl-PL')}</div>
            </div>
            <span className="pill" style={{ cursor: 'pointer' }} onClick={() => cycleStatus(c)}>{c.status}</span>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <input value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Dodaj komentarz…" />
          <button type="button" className="btn btn-sm" onClick={addComment}>Dodaj</button>
        </div>
      </div>
    </div>
  )
}

// ---- pojedyncza strona: outer div w rozmiarze przeskalowanym, inner w natywnym px + transform:scale ----
function PageSlot({ page, index, pxW, pxH, zoom, mounted, srcDoc, revision, onRef, onIframeRef }) {
  return (
    <div className="pdf-page-slot" data-page-index={index} ref={el => onRef(index, el)}>
      <div className="pdf-page-label">Strona {index + 1} · {page.partial_slug}</div>
      <div className="pdf-page-wrapper" style={{ width: pxW * zoom, height: pxH * zoom }}>
        {mounted ? (
          <iframe key={`${page.id}:${revision}`} ref={el => onIframeRef(page.id, el)} title={`strona ${index + 1}`}
            srcDoc={srcDoc} sandbox="allow-scripts"
            className="pdf-page-frame" style={{ width: pxW, height: pxH, transform: `scale(${zoom})` }} />
        ) : (
          <div className="pdf-page-placeholder" />
        )}
      </div>
    </div>
  )
}

// `composeJobId`/`setComposeJobId` są PODNIESIONE do rodzica (ProjectWorkspace) — tak samo jak
// `importJobId` w ContentTab (Faza 10): pozwala to rodzicowi wznowić polowanie po odświeżeniu
// strony (mount effect w ProjectWorkspace woła `api.activeJob`) i jednocześnie wiedzieć że trwa
// aktywna kompozycja (gating zakładek), bez duplikowania tego stanu.
export default function PageViewerEditor({ projectId, units, reloadAll, composeJobId, setComposeJobId }) {
  const [composing, setComposing] = useState(false)
  // Pokrywa i start ręczny (runCompose ustawia composeJobId), i wznowienie po odświeżeniu strony
  // (rodzic ustawia composeJobId z `api.activeJob` — ten efekt reaguje niezależnie od tego, KIEDY
  // ten komponent się zamontował względem tamtego zapytania).
  useEffect(() => { if (composeJobId != null) setComposing(true) }, [composeJobId])
  const [pages, setPages] = useState([])
  const [unassigned, setUnassigned] = useState([])
  const [preview, setPreview] = useState([])
  const [template, setTemplate] = useState(null)
  const [localUnits, setLocalUnits] = useState(units)
  const [err, setErr] = useState('')
  const [zoom, setZoom] = useState(1)
  const [activeIndex, setActiveIndex] = useState(0)
  const [pageInput, setPageInput] = useState('1')
  const [revisions, setRevisions] = useState({})
  const [toolbar, setToolbar] = useState(null)
  const [modalUnit, setModalUnit] = useState(null)
  const [search, setSearch] = useState('')

  const viewerRef = useRef()
  const pageRefs = useRef([])
  const iframeRefs = useRef({})
  const fileInputRef = useRef()
  const pendingImageTarget = useRef(null)
  const editingSlotRef = useRef(null)   // {pageId, slot} pod edycją, do wysyłania patcha po content-changed

  useEffect(() => { setLocalUnits(units) }, [units])

  const loadComposed = async () => {
    try {
      const [p, u, pv, proj] = await Promise.all([api.pages(projectId), api.unassigned(projectId), api.preview(projectId), api.project(projectId)])
      setPages(p); setUnassigned(u); setPreview(pv)
      const tpl = await api.template(proj.template_id)
      setTemplate(tpl)
    } catch (e) { setErr(e.message) }
  }
  useEffect(() => { loadComposed() }, [projectId])

  const composeJob = useJob(composeJobId, {
    onDone: () => { setComposeJobId(null); setComposing(false); loadComposed(); reloadAll() },
    onError: (j) => { setComposeJobId(null); setErr(j.error_msg || 'Kompozycja nie powiodła się'); setComposing(false) },
  })
  const composeElapsed = useJobElapsed(composeJob)
  const runCompose = async () => {
    setErr(''); setComposing(true)
    try { const res = await api.compose(projectId); setComposeJobId(res.job_id) }
    catch (e) { setErr(e.message); setComposing(false) }
  }

  const [fillingImages, setFillingImages] = useState(false)
  const [fillJobId, setFillJobId] = useState(null)
  const [fillMsg, setFillMsg] = useState('')
  useJob(fillJobId, {
    onDone: async () => {
      setFillJobId(null); setFillingImages(false)
      const before = localUnits.length
      loadComposed(); reloadAll()
      try {
        const fresh = await api.units(projectId)
        const added = fresh.length - before
        setFillMsg(added > 0 ? `Gotowe — wygenerowano ${added} ${added === 1 ? 'nową ilustrację' : 'nowe ilustracje'}.`
                             : 'Gotowe — nie było brakujących obrazów do uzupełnienia.')
      } catch { setFillMsg('Zadanie zakończone.') }
    },
    onError: (j) => { setFillJobId(null); setFillingImages(false); setErr(j.error_msg || 'Uzupełnianie obrazów nie powiodło się') },
  })
  const runFillImages = async () => {
    setErr(''); setFillMsg(''); setFillingImages(true)
    try { const res = await api.fillImages(projectId); setFillJobId(res.job_id) }
    catch (e) { setErr(e.message); setFillingImages(false) }
  }

  const unitsById = useMemo(() => Object.fromEntries(localUnits.map(u => [u.id, u])), [localUnits])
  const [pageW, pageH] = useMemo(() => pagePxDims(template), [template])

  // dopasuj szerokość automatycznie przy pierwszym załadowaniu stron/szablonu
  useEffect(() => {
    if (pages.length && template && viewerRef.current) {
      setZoom(fitWidthZoom(viewerRef.current.clientWidth - 48, pageW))
    }
  }, [pages.length, template, pageW])

  // --- śledzenie aktywnej strony (IntersectionObserver) ---
  useEffect(() => {
    if (!pages.length) return undefined
    const io = new IntersectionObserver((entries) => {
      let best = null
      entries.forEach(en => { if (en.isIntersecting && (!best || en.intersectionRatio > best.intersectionRatio)) best = en })
      if (best) {
        const idx = Number(best.target.dataset.pageIndex)
        setActiveIndex(idx); setPageInput(String(idx + 1))
      }
    }, { root: viewerRef.current, threshold: [0.15, 0.35, 0.55, 0.75] })
    pageRefs.current.forEach(el => el && io.observe(el))
    return () => io.disconnect()
  }, [pages.length])

  const scrollToIndex = (i) => {
    const idx = Math.max(0, Math.min(pages.length - 1, i))
    pageRefs.current[idx]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  const goToPage = () => {
    const n = parseInt(pageInput, 10)
    if (!Number.isNaN(n)) scrollToIndex(n - 1)
  }

  const fitWidth = () => viewerRef.current && setZoom(fitWidthZoom(viewerRef.current.clientWidth - 48, pageW))
  const fitHeight = () => viewerRef.current && setZoom(fitHeightZoom(viewerRef.current.clientWidth - 48, viewerRef.current.clientHeight - 48, pageW, pageH))

  // --- klawiatura: strzałki zmieniają stronę, ale NIE gdy fokus jest w polu tekstowym/iframe ---
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement && document.activeElement.tagName
      if (['IFRAME', 'INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); scrollToIndex(activeIndex - 1) }
      else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); scrollToIndex(activeIndex + 1) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [activeIndex, pages.length])

  // --- zapis treści po edycji live (debounce już zrobiony w iframe, tu zapisujemy od razu) ---
  const saveEditedContent = async (unitId, kind, existingContent, html, text) => {
    let content
    if (kind === 'bullet_list' || kind === 'numbered_list') {
      const tmp = document.createElement('div')
      tmp.innerHTML = html
      const lis = Array.from(tmp.querySelectorAll(':scope > li'))
      content = { ...existingContent, items: lis.map(li => li.innerText.trim()), items_html: lis.map(li => li.innerHTML) }
    } else {
      content = { ...existingContent, text, html }
    }
    try {
      const updated = await api.patchUnit(projectId, unitId, content)
      setLocalUnits(us => us.map(u => u.id === unitId ? updated : u))
    } catch (e) { setErr(e.message) }
  }

  // --- postMessage z podglądów stron (klik/zaznaczenie/edycja/obraz) ---
  useEffect(() => {
    const onMessage = (e) => {
      const d = e.data
      if (!d || d.source !== 'gpdf-preview') return
      let pageId = null
      for (const [pid, el] of Object.entries(iframeRefs.current)) {
        if (el && el.contentWindow === e.source) { pageId = pid; break }
      }
      const page = pages.find(p => p.id === pageId)
      const placement = page?.placements.find(pl => pl.slot_slug === d.slot)
      const unit = placement ? unitsById[placement.unit_id] : null

      if (d.type === 'click') {
        if (unit && !isEditableKind(unit.kind) && unit.kind !== 'image') setModalUnit(unit)
      } else if (d.type === 'selection') {
        const iframeEl = iframeRefs.current[pageId]
        if (!iframeEl) return
        const ir = iframeEl.getBoundingClientRect()
        setToolbar({
          visible: true, pageId, slot: d.slot, format: d.format,
          top: ir.top + d.rect.top * zoom - 44, left: ir.left + d.rect.left * zoom,
        })
        editingSlotRef.current = { pageId, slot: d.slot }
      } else if (d.type === 'content-changed') {
        if (unit) saveEditedContent(unit.id, unit.kind, unit.content || {}, d.html, d.text)
      } else if (d.type === 'blur') {
        setToolbar(t => (t && t.slot === d.slot ? { ...t, visible: false } : t))
      } else if (d.type === 'image-click') {
        if (unit) { pendingImageTarget.current = { unitId: unit.id, pageId }; fileInputRef.current?.click() }
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [pages, unitsById, zoom])

  const execToolbar = (cmd) => {
    const target = editingSlotRef.current
    if (!target) return
    const iframeEl = iframeRefs.current[target.pageId]
    if (!iframeEl) return
    if (cmd === 'createLink') {
      const url = window.prompt('Adres URL linku:', 'https://')
      if (!url) return
      iframeEl.contentWindow.postMessage({ type: 'exec', cmd: 'createLink', value: url }, '*')
    } else {
      iframeEl.contentWindow.postMessage({ type: 'exec', cmd }, '*')
    }
  }

  const bumpRevision = (pageId) => setRevisions(r => ({ ...r, [pageId]: (r[pageId] || 0) + 1 }))

  const refreshPreview = async (pageId) => {
    try {
      const pv = await api.preview(projectId)
      setPreview(pv)
      if (pageId) bumpRevision(pageId)
    } catch (e) { setErr(e.message) }
  }

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    const target = pendingImageTarget.current
    pendingImageTarget.current = null
    if (!file || !target) return
    try {
      const fd = new FormData(); fd.append('file', file)
      const updated = await api.uploadUnitImage(projectId, target.unitId, fd)
      setLocalUnits(us => us.map(u => u.id === updated.id ? updated : u))
      await refreshPreview(target.pageId)
    } catch (e2) { setErr(e2.message) }
  }

  const assignUnit = async (unassignedId, pageId, slotSlug) => {
    try { await api.assignUnassigned(projectId, unassignedId, { page_id: pageId, slot_slug: slotSlug }); await loadComposed(); bumpRevision(pageId) }
    catch (e) { setErr(e.message) }
  }

  const [changingPartial, setChangingPartial] = useState(false)
  const [changeJobId, setChangeJobId] = useState(null)
  const [layoutChoice, setLayoutChoice] = useState('')
  useJob(changeJobId, {
    onDone: async () => { setChangeJobId(null); setChangingPartial(false); const pid = pages[activeIndex]?.id; await loadComposed(); if (pid) bumpRevision(pid) },
    onError: (j) => { setErr(j.error_msg || 'Zmiana layoutu nie powiodła się'); setChangingPartial(false) },
  })
  const applyLayoutChange = async () => {
    const page = pages[activeIndex]
    if (!page || !layoutChoice) return
    setChangingPartial(true); setErr('')
    try { const res = await api.changePartial(projectId, page.id, layoutChoice); setChangeJobId(res.job_id) }
    catch (e) { setErr(e.message); setChangingPartial(false) }
  }

  // --- wyszukiwanie tekstu w dokumencie (po lokalnej kopii jednostek, zawsze zawiera plain text) ---
  const searchMatches = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (q.length < 2) return []
    const out = []
    for (const pg of pages) {
      for (const pl of pg.placements) {
        const u = unitsById[pl.unit_id]
        if (!u) continue
        const hay = (u.content?.text || (u.content?.items || []).join(' ') || '').toLowerCase()
        if (hay.includes(q)) out.push({ pageIndex: pg.order_index, pageId: pg.id, slot: pl.slot_slug })
      }
    }
    return out
  }, [search, pages, unitsById])

  const jumpToMatch = (m) => {
    scrollToIndex(m.pageIndex)
    setTimeout(() => { iframeRefs.current[m.pageId]?.contentWindow?.postMessage({ type: 'focus-slot', slot: m.slot }, '*') }, 400)
  }

  const currentPage = pages[activeIndex]
  const pageLayouts = (template?.partials || []).filter(p => p.is_page_template)

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn btn-primary" disabled={composing} onClick={runCompose}>
          {composing ? 'Składanie…' : (pages.length ? 'Przelicz skład ponownie' : '✦ Skomponuj dokument')}
        </button>
        {pages.length > 0 && (
          <button className="btn" disabled={fillingImages} onClick={runFillImages}>
            {fillingImages ? 'Generowanie ilustracji…' : 'Uzupełnij brakujące obrazy (AI)'}
          </button>
        )}
        <span className="muted small">{localUnits.length} jednostek treści · {pages.length} złożonych stron</span>
      </div>
      <Alert kind="err">{err}</Alert>
      <Alert kind="ok">{fillMsg}</Alert>
      {composing && (
        <Alert kind="info">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <span><span className="spin-sm" style={{ marginRight: 8 }} />Składanie dokumentu w toku (AI)…</span>
            <span className="mono small">{composeElapsed}</span>
          </div>
        </Alert>
      )}
      {composing && <JobProgress job={composeJob} showTime={false} />}

      {pages.length === 0 ? (
        <div className="empty">Brak jeszcze złożonego dokumentu — kliknij „Skomponuj dokument” powyżej (wymaga zaimportowanej treści w zakładce „Treść”).</div>
      ) : (
        <div className="editor-viewport">
          <div className="pdf-viewer" ref={viewerRef}>
            {pages.map((pg, i) => {
              const p = preview.find(x => x.page_id === pg.id)
              const mounted = Math.abs(i - activeIndex) <= 2
              const slotKinds = Object.fromEntries(pg.placements.map(pl => [pl.slot_slug, unitsById[pl.unit_id]?.kind]))
              return (
                <PageSlot key={pg.id} page={pg} index={i} pxW={pageW} pxH={pageH} zoom={zoom} mounted={mounted}
                  srcDoc={p ? injectEditingSupport(p.html, slotKinds) : ''} revision={revisions[pg.id] || 0}
                  onRef={(idx, el) => { pageRefs.current[idx] = el }}
                  onIframeRef={(pid, el) => { iframeRefs.current[pid] = el }} />
              )
            })}
          </div>

          <div className="editor-bottombar">
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => scrollToIndex(0)} title="Pierwsza strona">⏮</button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => scrollToIndex(activeIndex - 1)} title="Poprzednia strona">◀</button>
            <input className="page-num-input" value={pageInput} onChange={e => setPageInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && goToPage()} />
            <button type="button" className="btn btn-sm" onClick={goToPage}>Idź</button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => scrollToIndex(activeIndex + 1)} title="Następna strona">▶</button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => scrollToIndex(pages.length - 1)} title="Ostatnia strona">⏭</button>
            <span className="muted small" style={{ margin: '0 10px' }}>Strona {activeIndex + 1} z {pages.length}</span>

            <span className="bottombar-sep" />
            <button type="button" className="btn btn-sm btn-ghost" onClick={fitWidth}>Dopasuj szerokość</button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={fitHeight}>Dopasuj wysokość</button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setZoom(z => z / 1.15)}>−</button>
            <span className="muted small mono">{Math.round(zoom * 100)}%</span>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setZoom(z => z * 1.15)}>+</button>

            <span className="bottombar-sep" />
            <Popover label="🔍 Szukaj">
              <input placeholder="Szukaj w dokumencie…" value={search} onChange={e => setSearch(e.target.value)} autoFocus />
              <div className="muted small" style={{ margin: '8px 0' }}>{search.trim().length >= 2 ? `${searchMatches.length} trafień` : 'Wpisz co najmniej 2 znaki'}</div>
              <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                {searchMatches.map((m, i2) => (
                  <div key={i2} className="unit-row" style={{ cursor: 'pointer' }} onClick={() => jumpToMatch(m)}>
                    Strona {m.pageIndex + 1} — {m.slot}
                  </div>
                ))}
              </div>
            </Popover>

            <Popover label="Zmień layout tej strony">
              <select value={layoutChoice} onChange={e => setLayoutChoice(e.target.value)}>
                <option value="">— wybierz layout —</option>
                {pageLayouts.map(p => <option key={p.id} value={p.id}>{p.slug}</option>)}
              </select>
              <button type="button" className="btn btn-sm btn-primary" style={{ marginTop: 8 }}
                disabled={!layoutChoice || changingPartial} onClick={applyLayoutChange}>
                {changingPartial ? 'Remapowanie…' : 'Zastosuj'}
              </button>
            </Popover>

            <Popover label="Sloty na tej stronie">
              {!currentPage || currentPage.placements.length === 0 ? <div className="muted small">Brak wypełnionych slotów.</div> : (
                currentPage.placements.map(pl => {
                  const u = unitsById[pl.unit_id]
                  return (
                    <div key={pl.slot_slug} className="unit-row" style={{ cursor: u && !isEditableKind(u.kind) && u.kind !== 'image' ? 'pointer' : 'default' }}
                      onClick={() => { if (u && !isEditableKind(u.kind) && u.kind !== 'image') setModalUnit(u) }}>
                      <span className="kind-tag">{pl.slot_slug}</span>
                      <div className="small">{unitPreviewText(u)}</div>
                    </div>
                  )
                })
              )}
            </Popover>

            {unassigned.length > 0 && (
              <Popover label="Nieprzypisane" badge={unassigned.length}>
                {unassigned.map(u => (
                  <div key={u.id} className="unassigned-row">
                    <div className="small">{KIND_PL[unitsById[u.unit_id]?.kind] || unitsById[u.unit_id]?.kind}</div>
                    <div className="muted small" style={{ marginBottom: 8 }}>{u.reason}</div>
                    <AssignPicker page={currentPage} template={template} onAssign={(slot) => assignUnit(u.id, currentPage.id, slot)} />
                  </div>
                ))}
              </Popover>
            )}
          </div>
        </div>
      )}

      <RteToolbar toolbar={toolbar} onExec={execToolbar} />
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFileChosen} />
      {modalUnit && (
        <UnitDetailModal projectId={projectId} unit={modalUnit} onClose={() => setModalUnit(null)}
          onSaved={(updated) => { setLocalUnits(us => us.map(u => u.id === updated.id ? updated : u)); setModalUnit(null); const pid = pages.find(p => p.placements.some(pl => pl.unit_id === updated.id))?.id; if (pid) refreshPreview(pid) }} />
      )}
    </div>
  )
}
